import React from 'react'
import {
    StyledChevronIcon,
    StyledDoubleChevronIcon,
    StyledPaginationList,
} from '../Pagination.styled'
import { getNearbyPages, range } from '../helpers'
import { PaginationItem } from './PaginationItem'
import { NumberedPages } from './NumberedPages'
import { WrapperPages } from './WrapperPages'

interface PaginationContentProps {
    currentPage: number
    publicationCount: number
    pageSize: number
    onPageChange: (page: number) => void
    path: string
    isMobile: boolean
}
export const PaginationContent: React.FC<PaginationContentProps> = ({
    currentPage,
    publicationCount,
    pageSize,
    onPageChange,
    path,
    isMobile,
}) => {
    const pagesCount = Math.ceil(Math.min(publicationCount / pageSize, 10)) // Hard cap of 10 pages
    const pagesToDisplay = getNearbyPages(
        pagesCount,
        currentPage,
        isMobile ? 4 : 7,
    )
    const pages = range(pagesToDisplay.start, pagesToDisplay.end)
    const isFirstPage = currentPage === 1
    const isLastPage = currentPage === pagesCount
    const showEllipses = pagesCount > 7
    const isNearStart = currentPage > 4
    const isNearEnd = currentPage < pagesCount - 3

    return (
        <nav role="navigation" aria-label="Pagination Navigation">
            <StyledPaginationList>
                <PaginationItem
                    text={'First'}
                    isDisabled={isFirstPage}
                    layout={{
                        showText: false,
                        display: {
                            default: {
                                display: 'flex',
                            },
                            sm: {
                                display: 'none',
                            },
                        },
                    }}
                    onClick={onPageChange}
                    routePath={path}
                    icon={{
                        iconElement: (
                            <StyledDoubleChevronIcon
                                isLeft
                                isActive={isFirstPage}
                            />
                        ),
                        iconPosition: 'before',
                    }}
                    page={{ currentPage, nextPage: 1 }}
                />
                <PaginationItem
                    text={'Previous'}
                    isDisabled={isFirstPage}
                    layout={{
                        showText: true,
                        hideTextAtMobile: true,
                        display: {
                            default: {
                                display: 'flex',
                            },
                        },
                    }}
                    onClick={onPageChange}
                    routePath={path}
                    icon={{
                        iconElement: (
                            <StyledChevronIcon isLeft isActive={isFirstPage} />
                        ),
                        iconPosition: 'before',
                    }}
                    page={{ currentPage, nextPage: currentPage - 1 }}
                />
                {showEllipses && isNearStart && (
                    // Only rendered on desktop, shows First/Last page and ellipses
                    <WrapperPages
                        ellipsesPosition="after"
                        path={path}
                        isDisabled={isFirstPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        nextPage={1}
                    />
                )}
                <NumberedPages // Numbered pages that show between First / Last page buttons
                    pages={pages}
                    currentPage={currentPage}
                    path={path}
                    onPageChange={onPageChange}
                />
                {showEllipses && isNearEnd && (
                    // Only rendered on desktop, shows First/Last page and ellipses
                    <WrapperPages
                        ellipsesPosition="before"
                        path={path}
                        isDisabled={isLastPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        nextPage={pagesCount}
                    />
                )}
                <PaginationItem
                    text={'Next'}
                    isDisabled={isLastPage}
                    layout={{
                        showText: true,
                        hideTextAtMobile: true,
                        display: {
                            default: {
                                display: 'flex',
                            },
                        },
                    }}
                    onClick={onPageChange}
                    routePath={path}
                    icon={{
                        iconElement: (
                            <StyledChevronIcon isActive={isLastPage} />
                        ),
                        iconPosition: 'after',
                    }}
                    page={{ currentPage, nextPage: currentPage + 1 }}
                />
                <PaginationItem
                    text={'Last'}
                    isDisabled={isLastPage}
                    layout={{
                        showText: false,
                        display: {
                            default: {
                                display: 'flex',
                            },
                            sm: {
                                display: 'none',
                            },
                        },
                    }}
                    onClick={onPageChange}
                    routePath={path}
                    icon={{
                        iconElement: (
                            <StyledDoubleChevronIcon isActive={isLastPage} />
                        ),
                        iconPosition: 'after',
                    }}
                    page={{ currentPage, nextPage: pagesCount }}
                />
            </StyledPaginationList>
        </nav>
    )
}
